// WS3 — canonical-shape priority editor. The schema-bound fields
// (obligationTypeCode / recipientResolution / accountResolution /
// sourceAccountResolution / amountFormulaCode / isTakeAllRemaining)
// render read-only — full-schema authoring stays in code + seed. The
// admin's safe-edit subset on a draft is: reorder, isPersistent toggle,
// firesOnIncomeTypesJson multi-select, and an advanced conditionalLogicJson
// raw-JSON edit. The backend hard-rejects any draft saved in the legacy
// `priorityLevel / recipientRoleSlot / basisType` shape, so an empty
// editor on a fresh code is fine — the WF1/WF2 ladder is seeded.

import { useId } from "react";
import { Button, Label, Textarea } from "@/design-system/components/ui";
import { ArrowDown, ArrowUp, Info, Trash2 } from "lucide-react";
import type { PriorityRow } from "@/api/portfolioAdmin/pr6Types";
import {
  EVENT_TRIGGER_CODES,
  STANDARD_PAYMENT_TYPES,
  TRIGGER_CODE_DESCRIPTIONS,
} from "./triggerCodes";

/** Back-compat alias — the trigger catalog now lives in ./triggerCodes
 *  (mirrored from Core/Constants/WaterfallVocabularyDescriptions.cs). */
export const PAYMENT_TYPES = STANDARD_PAYMENT_TYPES;

interface PriorityRowsEditorProps {
  rows: PriorityRow[];
  editable: boolean;
  onChange: (next: PriorityRow[]) => void;
  /** When provided, each row renders a "Details" affordance (editable AND
   *  read-only modes) opening the priority drill-down drawer. */
  onShowDetail?: (position: number) => void;
}

function reposition(rows: PriorityRow[]): PriorityRow[] {
  return rows.map((r, i) => ({ ...r, priorityPosition: i + 1 }));
}

function parseFiresOn(json: string): string[] {
  try {
    const parsed = JSON.parse(json || "[]");
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

export function PriorityRowsEditor({
  rows,
  editable,
  onChange,
  onShowDetail,
}: PriorityRowsEditorProps) {
  const move = (index: number, delta: number) => {
    const j = index + delta;
    if (j < 0 || j >= rows.length) return;
    const next = [...rows];
    [next[index], next[j]] = [next[j], next[index]];
    onChange(reposition(next));
  };
  const remove = (index: number) => {
    if (!confirm("Remove this priority row?")) return;
    const next = rows.filter((_, i) => i !== index);
    onChange(reposition(next));
  };
  const update = (index: number, patch: Partial<PriorityRow>) => {
    const next = rows.map((r, i) => (i === index ? { ...r, ...patch } : r));
    onChange(next);
  };

  return (
    <div
      className="flex flex-col gap-space-2"
      data-testid="priority-rows-editor"
    >
      {rows.length === 0 ? (
        <p className="text-small text-gray-mid italic m-0">
          No priorities defined yet.
        </p>
      ) : (
        <ul className="flex flex-col gap-space-2 m-0 p-0 list-none">
          {rows.map((r, i) => (
            <li key={`${r.priorityPosition}-${r.obligationTypeCode}-${i}`}>
              <PriorityRowFields
                row={r}
                editable={editable}
                onUpdate={(patch) => update(i, patch)}
                onMoveUp={() => move(i, -1)}
                onMoveDown={() => move(i, 1)}
                onRemove={() => remove(i)}
                onShowDetail={onShowDetail}
                isFirst={i === 0}
                isLast={i === rows.length - 1}
              />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface PriorityRowFieldsProps {
  row: PriorityRow;
  editable: boolean;
  onUpdate: (patch: Partial<PriorityRow>) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onRemove: () => void;
  onShowDetail?: (position: number) => void;
  isFirst: boolean;
  isLast: boolean;
}

function PriorityRowFields({
  row,
  editable,
  onUpdate,
  onMoveUp,
  onMoveDown,
  onRemove,
  onShowDetail,
  isFirst,
  isLast,
}: PriorityRowFieldsProps) {
  const obligationId = useId();
  const recipientId = useId();
  const formulaId = useId();
  const firesOnId = useId();
  const conditionalId = useId();
  const persistentId = useId();
  const firingSet = parseFiresOn(row.firesOnIncomeTypesJson);
  const toggleFires = (code: string) => {
    const set = new Set(firingSet);
    if (set.has(code)) set.delete(code);
    else set.add(code);
    onUpdate({ firesOnIncomeTypesJson: JSON.stringify(Array.from(set)) });
  };

  return (
    <div
      className="border border-border-light rounded-md p-space-3 bg-white-pure"
      data-testid={`priority-row-${row.priorityPosition}`}
    >
      <div className="flex items-start gap-space-3">
        <div className="flex flex-col items-center gap-space-1">
          <span
            className="font-display font-bold text-h4 text-gold-shadow"
            data-testid={`priority-row-${row.priorityPosition}-position`}
          >
            #{row.priorityPosition}
          </span>
          {editable && (
            <div className="flex flex-col gap-[2px]">
              <button
                type="button"
                onClick={onMoveUp}
                disabled={isFirst}
                className="text-gray-mid disabled:opacity-30 hover:text-onyx"
                aria-label="Move priority up"
                data-testid={`priority-row-${row.priorityPosition}-up`}
              >
                <ArrowUp size={14} />
              </button>
              <button
                type="button"
                onClick={onMoveDown}
                disabled={isLast}
                className="text-gray-mid disabled:opacity-30 hover:text-onyx"
                aria-label="Move priority down"
                data-testid={`priority-row-${row.priorityPosition}-down`}
              >
                <ArrowDown size={14} />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-space-2 flex-1">
          {/* Schema-bound fields — display only. */}
          <ReadOnlyField
            label="Obligation type"
            id={obligationId}
            value={row.obligationTypeCode || "—"}
            testId={`priority-row-${row.priorityPosition}-obligation`}
          />
          <ReadOnlyField
            label="Recipient resolver"
            id={recipientId}
            value={row.recipientResolution || "—"}
            testId={`priority-row-${row.priorityPosition}-recipient`}
          />
          <ReadOnlyField
            label="Account resolver"
            value={row.accountResolution || "—"}
            testId={`priority-row-${row.priorityPosition}-account`}
          />
          <ReadOnlyField
            label="Source account"
            value={row.sourceAccountResolution || "—"}
            testId={`priority-row-${row.priorityPosition}-source`}
          />
          <ReadOnlyField
            label="Amount formula"
            id={formulaId}
            value={row.amountFormulaCode || "—"}
            testId={`priority-row-${row.priorityPosition}-formula`}
          />
          <ReadOnlyField
            label="Take all remaining"
            value={row.isTakeAllRemaining ? "Yes" : "No"}
            testId={`priority-row-${row.priorityPosition}-tar`}
          />

          {/* Safe-edit subset. Two grouped sets mirroring the backend's
              trigger-code catalog: the nine ServicingPaymentType codes,
              then the event / engine trigger codes. Any code stored in
              the row's JSON but unknown to the catalog still renders as
              an extra checked chip so nothing is ever invisible. */}
          <div className="flex flex-col gap-space-1 md:col-span-2">
            <Label htmlFor={firesOnId}>Fires on income types</Label>
            <div
              id={firesOnId}
              className="flex flex-col gap-space-2"
              data-testid={`priority-row-${row.priorityPosition}-fires-on`}
            >
              <TriggerChipGroup
                heading="Payment types"
                codes={STANDARD_PAYMENT_TYPES}
                firingSet={firingSet}
                editable={editable}
                onToggle={toggleFires}
                position={row.priorityPosition}
                testId={`priority-row-${row.priorityPosition}-fires-payment-group`}
              />
              <TriggerChipGroup
                heading="Event / engine triggers"
                codes={[
                  ...EVENT_TRIGGER_CODES,
                  // Unknown-to-the-catalog codes render (checked) here.
                  ...firingSet.filter(
                    (c) =>
                      !(STANDARD_PAYMENT_TYPES as readonly string[]).includes(c) &&
                      !(EVENT_TRIGGER_CODES as readonly string[]).includes(c),
                  ),
                ]}
                firingSet={firingSet}
                editable={editable}
                onToggle={toggleFires}
                position={row.priorityPosition}
                testId={`priority-row-${row.priorityPosition}-fires-event-group`}
              />
            </div>
          </div>

          <label
            className="flex items-center gap-space-2 text-small md:col-span-2"
            htmlFor={persistentId}
          >
            <input
              id={persistentId}
              type="checkbox"
              checked={row.isPersistent}
              disabled={!editable}
              onChange={(e) => onUpdate({ isPersistent: e.target.checked })}
              data-testid={`priority-row-${row.priorityPosition}-persistent`}
            />
            <span>Persistent (carries balance across runs)</span>
          </label>

          <div className="flex flex-col gap-space-1 md:col-span-2">
            <Label htmlFor={conditionalId}>Conditional logic (JSON)</Label>
            <Textarea
              id={conditionalId}
              rows={2}
              value={row.conditionalLogicJson ?? ""}
              disabled={!editable}
              placeholder="Advanced — leave blank for unconditional firing"
              onChange={(e) =>
                onUpdate({ conditionalLogicJson: e.target.value || null })
              }
              data-testid={`priority-row-${row.priorityPosition}-conditional`}
            />
          </div>
        </div>

        <div className="flex flex-col items-end gap-space-2 shrink-0">
          {onShowDetail && (
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              onClick={() => onShowDetail(row.priorityPosition)}
              data-testid={`priority-row-${row.priorityPosition}-details`}
            >
              <Info size={14} aria-hidden /> Details
            </Button>
          )}
          {editable && (
            <button
              type="button"
              onClick={onRemove}
              className="text-status-error hover:opacity-80"
              aria-label="Remove priority"
              data-testid={`priority-row-${row.priorityPosition}-remove`}
            >
              <Trash2 size={16} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** One visually grouped set of fires-on chips. Each chip carries the
 *  catalog description as a hover tooltip. */
function TriggerChipGroup({
  heading,
  codes,
  firingSet,
  editable,
  onToggle,
  position,
  testId,
}: {
  heading: string;
  codes: readonly string[];
  firingSet: string[];
  editable: boolean;
  onToggle: (code: string) => void;
  position: number;
  testId: string;
}) {
  return (
    <div className="flex flex-col gap-space-1" data-testid={testId}>
      <span className="text-caption uppercase tracking-wide text-gray-mid">
        {heading}
      </span>
      <div className="flex flex-wrap gap-space-2">
        {codes.map((code) => {
          const checked = firingSet.includes(code);
          return (
            <label
              key={code}
              title={TRIGGER_CODE_DESCRIPTIONS[code]}
              className={
                "inline-flex items-center gap-space-1 px-space-2 h-7 border border-border-light rounded-md text-caption " +
                (checked
                  ? "bg-gold-highlight/10 border-gold text-onyx"
                  : "bg-white-pure text-gray-mid") +
                (editable ? " cursor-pointer hover:border-gold" : " opacity-70")
              }
            >
              <input
                type="checkbox"
                checked={checked}
                disabled={!editable}
                onChange={() => onToggle(code)}
                data-testid={`priority-row-${position}-fires-${code}`}
              />
              {code}
            </label>
          );
        })}
      </div>
    </div>
  );
}

function ReadOnlyField({
  label,
  id,
  value,
  testId,
}: {
  label: string;
  id?: string;
  value: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-space-1">
      <Label htmlFor={id}>{label}</Label>
      <span
        id={id}
        className="h-10 px-space-2 inline-flex items-center border border-border-light rounded-md bg-white-warm text-small font-mono text-onyx"
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}
